import { Injectable } from '@angular/core';
import { SplitFactory } from '@splitsoftware/splitio';
import { UserService } from './user.service';
import { SegmentCMOSService } from './segment-cmos.service';
import { getSplitAttributes, getSplitConfiguration } from '../util/split-configuration';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, map } from 'rxjs/operators';

function fromTreatmentString(treatment: SplitIO.Treatment, defaultTreatment: boolean): boolean {
  if (treatment === 'on') {
    return true;
  } else if (treatment === 'off') {
    return false;
  } else {
    return defaultTreatment;
  }
}

@Injectable({
  providedIn: 'root'
})
export class SplitService {
  private client: SplitIO.IBrowserClient;

  constructor(private userService: UserService,
              private readonly segmentService: SegmentCMOSService) {
    this.userService.user
        .pipe(map(user => user?.userInfo?.externalId), distinctUntilChanged(isEqual))
        .subscribe(userId => this.reInitializeSplit(userId));
  }

  /**
   * Determines if feature is enabled via Split
   * @param splitFeatureName Split feature name to check status for
   * @param defaultFeatureState Default return value if Split is not available
   * @param attributes Optional attributes to customize targeting rules
   * @returns returns 'true' if split feature is on or enabled - otherwise 'false'
   */
  async isFeatureEnabled(splitFeatureName: string, defaultFeatureState: boolean = false, attributes?: {}): Promise<boolean> {
    try {
      await this.client.ready();
      const regionId = this.userService.getCurrentRegionId();
      const allAttributes = getSplitAttributes(attributes, regionId);

      const treatment = this.client.getTreatment(splitFeatureName, allAttributes);
      return fromTreatmentString(treatment, defaultFeatureState);
    } catch(err) {
      this.segmentService.addEvent('split_getTreatmentError', { reason: err.message });
      return defaultFeatureState;
    }
  }

  private reInitializeSplit(userId: string) {
    try {
      if (userId) {
        // Instantiate the SDK
        const factory = SplitFactory(getSplitConfiguration(userId));
        // And get the client instance you'll use
        this.client = factory.client();
      } else {
        // null userId implies user has logged out -> clean-up Split SDK
        this.client?.destroy();
        this.client = null;
      }
    } catch (err) {
      this.segmentService.addEvent('split_initializationError', { reason: err.message });
    }
  }

  overrideClient(clientOverride: SplitIO.IBrowserClient) {
    this.client = clientOverride;
  }
}
